import { useQueryClient } from '@tanstack/react-query';
import { useCreateTaskSubmission, createTaskSubmission } from '@/common/api/endpoints/submissions/submissions';
import { getGetTasksByCourseModuleQueryKey } from '@/common/api/endpoints/tasks/tasks';
import log from '@/common/utils/logger';

/**
 * Another try might succeed: the request never got an HTTP status, it timed out,
 * the server asked us to slow down, or the server failed. A 4xx rejection is final.
 */
export function isRetryableSubmissionError(error) {
  const code = error?.response?.data?.error?.code;
  if (code === 'SUBMISSION_MODIFIED_CONCURRENTLY') return false;

  const status = error?.response?.status;
  if (status == null || status === 0) return true;
  if (status === 408 || status === 429) return true;
  if (status >= 500 && status <= 599) return true;
  return false;
}

export function shouldRetrySubmissionMutation(failureCount, error) {
  if (!isRetryableSubmissionError(error)) return false;
  return failureCount < 3;
}

/**
 * Hook for updating task submissions with optimistic updates
 */
export const useUpdateSubmission = ({ courseId, moduleId }) => {
  const queryClient = useQueryClient();
  const queryKey = getGetTasksByCourseModuleQueryKey(courseId, moduleId, { include: 'submissions,resources' });

  const updateSubmissionMutation = useCreateTaskSubmission({
    mutation: {
      mutationFn: (variables) => {
        const {
          courseId: cid, moduleId: mid, taskId, data, signal, params,
        } = variables;
        return createTaskSubmission(cid, mid, taskId, data, { signal, params });
      },
      retry: (failureCount, error) => {
        const is401 = error?.response?.status === 401;
        if (is401) {
          log.error('[useUpdateSubmission] 401 error reached mutation (interceptor did NOT catch it)', {
            attemptNumber: failureCount + 1,
            errorCode: error?.response?.data?.error?.code,
            errorMessage: error?.response?.data?.error?.message,
            fullResponseData: error?.response?.data,
            responseStatus: error?.response?.status,
            responseHeaders: error?.response?.headers,
            requestUrl: error?.config?.url,
            requestMethod: error?.config?.method,
            hasAuthHeader: !!error?.config?.headers?.Authorization,
          });
        }

        const willRetry = shouldRetrySubmissionMutation(failureCount, error);
        log.info(`[useUpdateSubmission] Mutation attempt ${failureCount + 1}`, {
          status: error?.response?.status,
          errorCode: error?.response?.data?.error?.code,
          willRetry,
        });
        return willRetry;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      onError: (err, updatedSubmissionData, context) => {
        // Revert optimistic update on error
        if (context?.previousTasks) {
          queryClient.setQueryData(queryKey, context.previousTasks);
        }
      },

      onSuccess: (response) => {
        const updatedSubmission = response.data.data;
        // Check if the submission is marked as done based on state
        const isTaskMarkedDone = updatedSubmission.state === 'marked_as_done';

        // The new ETag from the server (module-scoped - all tasks share this!)
        const newEtag = updatedSubmission.etag;

        // Patch in updated submission AND propagate ETag to ALL sibling tasks
        // (ETag is module-scoped, so when we save any task, all tasks get the new ETag)
        queryClient.setQueryData(queryKey, (prevTasks) => {
          const updatedTasks = prevTasks?.data?.data?.map((t) => {
            if (t.id === updatedSubmission.taskId) {
              // Use the server response directly for the saved task
              return {
                ...t,
                submission: updatedSubmission,
              };
            }
            // For sibling tasks, propagate the new ETag (module-scoped)
            if (t.submission && newEtag) {
              return {
                ...t,
                submission: { ...t.submission, etag: newEtag },
              };
            }
            return t;
          }) || [];

          return {
            ...prevTasks,
            data: { data: updatedTasks },
          };
        });

        // Essential: If we've completed a task, we should ALWAYS refetch to ensure
        // server-side business logic has run correctly
        if (isTaskMarkedDone) {
          // This immediate refetch is essential for consistency
          // but we'll use a slight delay to avoid UI jank
          setTimeout(() => {
            queryClient.invalidateQueries(queryKey);
          }, 100);
        }
      },

      onSettled: () => {
        // For non-critical updates, just mark as stale without immediate refetch
        queryClient.invalidateQueries({
          queryKey,
          refetchType: 'inactive',
        });
      },
    },
  });

  return updateSubmissionMutation;
};

export default useUpdateSubmission;
